import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Loader2,
  Building2,
  Phone,
  Mail,
  MapPin,
  User,
  Save,
  StickyNote,
  Layers,
  CalendarRange,
} from "lucide-react";
import { createBranch } from "../../../api/hrm/branches/info";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1";
const inputClass =
  "w-full bg-white border border-gray-300 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all hover:border-gray-400 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5";

const Section = ({ icon: Icon, title, subTitle, children }) => (
  <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
    <div className="flex items-center gap-3">
      <div className="rounded-xl border border-red-700/10 bg-red-700/5 p-2 text-red-700">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-black uppercase tracking-wide text-gray-800">
          {title}
        </h3>
        {subTitle ? (
          <p className="mt-0.5 text-[11px] text-gray-400">{subTitle}</p>
        ) : null}
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className={`${labelClass} mb-1.5 block`}>
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    {children}
  </div>
);

const AFFILIATION_OPTIONS = [
  { value: "affiliated", label: "Trực thuộc" },
  { value: "hq", label: "Trụ sở / HQ" },
  { value: "franchise", label: "Nhượng quyền" },
];

const emptyForm = () => ({
  name: "",
  address: "",
  email: "",
  phone: "",
  manager: "",
  manager_phone: "",
  affiliation_type: "affiliated",
  franchise_start_date: "",
  franchise_end_date: "",
  note: "",
});

const BranchCreateModal = ({ isOpen, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (isOpen) setFormData(emptyForm());
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = async () => {
    if (loading) return;
    const confirm = await showConfirmDialog({
      title: "Xác nhận huỷ",
      text: "Bạn có chắc chắn muốn huỷ thao tác này?",
      confirmText: "Đồng ý",
      cancelText: "Giữ lại",
    });
    if (confirm.isConfirmed) onClose();
  };

  const handleSave = async () => {
    const {
      name,
      address,
      email,
      affiliation_type,
      franchise_start_date,
      franchise_end_date,
    } = formData;
    if (!name?.trim() || !address?.trim() || !email?.trim()) {
      showErrorToast("Vui lòng nhập đầy đủ Tên chi nhánh, Địa chỉ và Email");
      return;
    }
    if (affiliation_type === "franchise") {
      const hasStart = Boolean(franchise_start_date?.trim());
      const hasEnd = Boolean(franchise_end_date?.trim());
      if (hasStart !== hasEnd) {
        showErrorToast(
          "Nhượng quyền: nhập đủ Từ ngày và Đến ngày, hoặc để trống cả hai."
        );
        return;
      }
      if (hasStart && hasEnd && franchise_start_date > franchise_end_date) {
        showErrorToast("Ngày bắt đầu phải trước hoặc trùng ngày kết thúc.");
        return;
      }
    }

    try {
      setLoading(true);
      const payload = { ...formData };
      if (payload.affiliation_type !== "franchise") {
        payload.franchise_start_date = "";
        payload.franchise_end_date = "";
      }
      await createBranch(payload);
      showSuccessToast("Thêm chi nhánh thành công");
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Lỗi tạo chi nhánh:", err);
      showErrorToast("Không thể tạo chi nhánh");
    } finally {
      setLoading(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={() => !loading && onClose()}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Building2
                      className="h-5 w-5 sm:h-6 sm:w-6"
                      strokeWidth={2.25}
                    />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      Thêm <span className="text-red-700">chi nhánh</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Địa điểm, loại hình và người đại diện
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => !loading && onClose()}
                  disabled={loading}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-slate-50/40 px-4 py-4 sm:space-y-5 sm:px-6 sm:py-5">
                <Section
                  icon={Building2}
                  title="Thông tin chi nhánh"
                  subTitle="Tên, địa chỉ và kênh liên hệ chính."
                >
                  <Field label="Tên chi nhánh" required>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      type="text"
                      placeholder="VD: IMA HỒ CHÍ MINH"
                      className={`${inputClass} font-semibold`}
                    />
                  </Field>

                  <Field label="Địa chỉ" required>
                    <div className="relative">
                      <MapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        type="text"
                        placeholder="Số nhà, đường, quận/huyện..."
                        className={`${inputClass} pl-10`}
                      />
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Email" required>
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          type="email"
                          placeholder="chi-nhanh@domain.com"
                          className={`${inputClass} pl-10`}
                        />
                      </div>
                    </Field>
                    <Field label="Hotline">
                      <div className="relative">
                        <Phone className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          type="tel"
                          placeholder="028-xxxx / 09xx..."
                          className={`${inputClass} pl-10`}
                        />
                      </div>
                    </Field>
                  </div>
                </Section>

                <Section
                  icon={Layers}
                  title="Phân loại chi nhánh"
                  subTitle="Phạm vi vận hành và e-learning theo mô hình."
                >
                  <Field label="Loại hình">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {AFFILIATION_OPTIONS.map((opt) => {
                        const active = formData.affiliation_type === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                affiliation_type: opt.value,
                              }))
                            }
                            className={`rounded-2xl border px-3 py-3 text-xs font-bold uppercase tracking-wide transition ${
                              active
                                ? "border-red-300 bg-red-50 text-red-700 shadow-sm"
                                : "border-gray-200 bg-white text-gray-500 hover:border-red-200"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  {formData.affiliation_type === "franchise" ? (
                    <div className="space-y-4 rounded-2xl border border-amber-100 bg-amber-50/50 p-3.5 sm:p-4">
                      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-amber-800">
                        <CalendarRange className="h-4 w-4 shrink-0" />
                        Thời hạn nhượng quyền (tuỳ chọn)
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Từ ngày">
                          <input
                            name="franchise_start_date"
                            type="date"
                            value={formData.franchise_start_date}
                            onChange={handleChange}
                            className={`${inputClass} font-mono`}
                          />
                        </Field>
                        <Field label="Đến ngày">
                          <input
                            name="franchise_end_date"
                            type="date"
                            value={formData.franchise_end_date}
                            onChange={handleChange}
                            min={formData.franchise_start_date || undefined}
                            className={`${inputClass} font-mono`}
                          />
                        </Field>
                      </div>
                      <p className="text-[11px] leading-relaxed text-amber-800/80">
                        Để trống nếu chưa khai báo hợp đồng; hoặc nhập đủ hai
                        ngày để phục vụ thống kê và nhắc hết hạn.
                      </p>
                    </div>
                  ) : null}
                </Section>

                <Section
                  icon={User}
                  title="Người đại diện"
                  subTitle="Thông tin liên hệ trực tiếp tại chi nhánh."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Họ và tên">
                      <input
                        name="manager"
                        value={formData.manager}
                        onChange={handleChange}
                        type="text"
                        placeholder="Nguyễn Văn A"
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Số điện thoại đại diện">
                      <div className="relative">
                        <Phone className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                          name="manager_phone"
                          value={formData.manager_phone}
                          onChange={handleChange}
                          type="tel"
                          placeholder="09xx..."
                          className={`${inputClass} pl-10`}
                        />
                      </div>
                    </Field>
                  </div>
                </Section>

                <Section
                  icon={StickyNote}
                  title="Ghi chú"
                  subTitle="Thông tin bổ sung (tuỳ chọn)."
                >
                  <Field label="Ghi chú nội bộ">
                    <textarea
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Ghi chú thêm nếu cần..."
                      className={`${inputClass} min-h-[88px] resize-none`}
                    />
                  </Field>
                </Section>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-4 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  className="rounded-full border border-gray-200 bg-white px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 sm:py-2.5"
                >
                  Huỷ bỏ
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-3 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 sm:py-2.5"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  {loading ? "Đang xử lý..." : "Lưu chi nhánh"}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default BranchCreateModal;
